import {
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Stack,
    Textarea,
    useToast,
    Box,
    HStack,
    Text,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useState } from 'react';
import { RiLightbulbLine, RiStarLine, RiChat1Line } from 'react-icons/ri';
import { FeatureRequestFeedbackType, useCreateFeatureRequestFeedbackMutation } from '../../generated/graphql';

// Subtle gradient animation
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

export const FeatureRequestModal = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) => {
    const toast = useToast();
    const [createFeatureRequestFeedback, { loading }] = useCreateFeatureRequestFeedbackMutation();
    const [type, setType] = useState<FeatureRequestFeedbackType>();
    const [description, setDescription] = useState<string>('');

    const customOnClose = () => {
        setType(undefined);
        setDescription('');
        onClose();
    };

    const handleSubmit = () => {
        if (!type || !description) return;

        createFeatureRequestFeedback({
            variables: {
                description,
                type,
            } as any,
            onCompleted: () => {
                toast({
                    title: 'Thank you!',
                    description: 'Your submission has been received.',
                    status: 'success',
                    duration: 3000,
                });
                customOnClose();
            },
        });
    };

    const isFeatureSelected = type === FeatureRequestFeedbackType.Feature;
    const isFeedbackSelected = type === FeatureRequestFeedbackType.Feedback;

    return (
        <Modal isOpen={isOpen} onClose={customOnClose} size="md" isCentered motionPreset="slideInBottom">
            <ModalOverlay
                bg="blackAlpha.600"
                backdropFilter="blur(10px)"
            />
            <ModalContent
                bg="transparent"
                boxShadow="none"
                mx={4}
                overflow="visible"
            >
                {/* Outer glow effect */}
                <Box
                    position="absolute"
                    top="-2px"
                    left="-2px"
                    right="-2px"
                    bottom="-2px"
                    borderRadius="28px"
                    bg="linear-gradient(135deg, rgba(0,71,125,0.3) 0%, rgba(72,104,132,0.2) 50%, rgba(0,71,125,0.3) 100%)"
                    backgroundSize="200% 200%"
                    animation={`${gradientShift} 5s ease infinite`}
                    filter="blur(8px)"
                    opacity={0.6}
                    zIndex={-1}
                />

                {/* Main modal container */}
                <Box
                    bg="white"
                    borderRadius="24px"
                    overflow="hidden"
                    boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                >
                    {/* Header with gradient accent */}
                    <Box
                        position="relative"
                        overflow="hidden"
                    >
                        {/* Gradient bar */}
                        <Box
                            h="4px"
                            bgGradient="linear(to-r, #00477D, #486884, #00477D)"
                            backgroundSize="200% 100%"
                            animation={`${gradientShift} 3s ease infinite`}
                        />

                        <Flex
                            px={6}
                            pt={6}
                            pb={4}
                            align="center"
                            gap={4}
                        >
                            {/* Icon with gradient background */}
                            <Flex
                                w="56px"
                                h="56px"
                                borderRadius="16px"
                                bgGradient="linear(135deg, #00477D 0%, #486884 100%)"
                                align="center"
                                justify="center"
                                boxShadow="0 4px 14px rgba(0,71,125,0.3)"
                            >
                                <RiLightbulbLine size={28} color="white" />
                            </Flex>

                            <Box flex={1}>
                                <Text
                                    fontSize="20px"
                                    fontWeight={700}
                                    color="#1a1a2e"
                                    letterSpacing="-0.02em"
                                >
                                    Share Your Ideas
                                </Text>
                                <Text
                                    fontSize="14px"
                                    color="#64748b"
                                    mt={0.5}
                                >
                                    Help us improve Helivan
                                </Text>
                            </Box>

                            <ModalCloseButton
                                position="relative"
                                top={0}
                                right={0}
                                w="36px"
                                h="36px"
                                borderRadius="12px"
                                bg="#f1f5f9"
                                color="#64748b"
                                _hover={{ bg: '#e2e8f0', color: '#1a1a2e' }}
                                transition="all 0.2s"
                            />
                        </Flex>
                    </Box>

                    {/* Body */}
                    <ModalBody px={6} py={5}>
                        <Stack spacing={5}>
                            {/* Type Selection Cards */}
                            <HStack spacing={3}>
                                <Box
                                    flex={1}
                                    bg={isFeatureSelected ? 'rgba(0,71,125,0.08)' : '#f8fafc'}
                                    borderRadius="16px"
                                    p={5}
                                    border="2px solid"
                                    borderColor={isFeatureSelected ? '#00477D' : '#e2e8f0'}
                                    cursor="pointer"
                                    onClick={() => setType(FeatureRequestFeedbackType.Feature)}
                                    transition="all 0.2s ease"
                                    _hover={{
                                        borderColor: '#00477D',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(0,71,125,0.15)',
                                    }}
                                >
                                    <Flex direction="column" align="center" gap={3}>
                                        <Flex
                                            w="48px"
                                            h="48px"
                                            borderRadius="14px"
                                            bgGradient={isFeatureSelected
                                                ? 'linear(135deg, #00477D 0%, #486884 100%)'
                                                : 'none'}
                                            bg={isFeatureSelected ? undefined : '#e2e8f0'}
                                            align="center"
                                            justify="center"
                                            boxShadow={isFeatureSelected ? '0 4px 12px rgba(0,71,125,0.3)' : 'none'}
                                            transition="all 0.2s"
                                        >
                                            <RiStarLine
                                                size={22}
                                                color={isFeatureSelected ? 'white' : '#64748b'}
                                            />
                                        </Flex>
                                        <Text
                                            fontSize="15px"
                                            fontWeight={600}
                                            color={isFeatureSelected ? '#00477D' : '#1a1a2e'}
                                        >
                                            Feature
                                        </Text>
                                        <Text
                                            fontSize="13px"
                                            color="#64748b"
                                            textAlign="center"
                                        >
                                            Request a new feature
                                        </Text>
                                    </Flex>
                                </Box>

                                <Box
                                    flex={1}
                                    bg={isFeedbackSelected ? 'rgba(0,71,125,0.08)' : '#f8fafc'}
                                    borderRadius="16px"
                                    p={5}
                                    border="2px solid"
                                    borderColor={isFeedbackSelected ? '#00477D' : '#e2e8f0'}
                                    cursor="pointer"
                                    onClick={() => setType(FeatureRequestFeedbackType.Feedback)}
                                    transition="all 0.2s ease"
                                    _hover={{
                                        borderColor: '#00477D',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(0,71,125,0.15)',
                                    }}
                                >
                                    <Flex direction="column" align="center" gap={3}>
                                        <Flex
                                            w="48px"
                                            h="48px"
                                            borderRadius="14px"
                                            bgGradient={isFeedbackSelected
                                                ? 'linear(135deg, #00477D 0%, #486884 100%)'
                                                : 'none'}
                                            bg={isFeedbackSelected ? undefined : '#e2e8f0'}
                                            align="center"
                                            justify="center"
                                            boxShadow={isFeedbackSelected ? '0 4px 12px rgba(0,71,125,0.3)' : 'none'}
                                            transition="all 0.2s"
                                        >
                                            <RiChat1Line
                                                size={22}
                                                color={isFeedbackSelected ? 'white' : '#64748b'}
                                            />
                                        </Flex>
                                        <Text
                                            fontSize="15px"
                                            fontWeight={600}
                                            color={isFeedbackSelected ? '#00477D' : '#1a1a2e'}
                                        >
                                            Feedback
                                        </Text>
                                        <Text
                                            fontSize="13px"
                                            color="#64748b"
                                            textAlign="center"
                                        >
                                            Share your thoughts
                                        </Text>
                                    </Flex>
                                </Box>
                            </HStack>

                            {/* Description Input */}
                            {type && (
                                <Box>
                                    <Text
                                        fontSize="13px"
                                        fontWeight={600}
                                        color="#475569"
                                        mb={3}
                                        textTransform="uppercase"
                                        letterSpacing="0.05em"
                                    >
                                        {isFeatureSelected
                                            ? 'Describe the feature you would like'
                                            : 'Share your feedback'}
                                    </Text>
                                    <Textarea
                                        placeholder={isFeatureSelected
                                            ? 'I would love to see a feature that...'
                                            : 'Here is my feedback about...'
                                        }
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows={5}
                                        size="lg"
                                        bg="#f8fafc"
                                        border="2px solid"
                                        borderColor="#e2e8f0"
                                        borderRadius="14px"
                                        fontSize="15px"
                                        color="#1a1a2e"
                                        _placeholder={{ color: '#94a3b8' }}
                                        _hover={{ borderColor: '#cbd5e1' }}
                                        _focus={{
                                            borderColor: '#00477D',
                                            boxShadow: '0 0 0 3px rgba(0,71,125,0.1)',
                                            bg: 'white',
                                        }}
                                        transition="all 0.2s"
                                        resize="none"
                                    />
                                </Box>
                            )}
                        </Stack>
                    </ModalBody>

                    {/* Footer */}
                    <Flex
                        px={6}
                        py={5}
                        bg="#f8fafc"
                        borderTop="1px solid"
                        borderColor="#e2e8f0"
                        gap={3}
                        justify="flex-end"
                    >
                        <Box
                            as="button"
                            px={5}
                            py={2.5}
                            borderRadius="12px"
                            fontSize="14px"
                            fontWeight={600}
                            color="#64748b"
                            bg="white"
                            border="1px solid"
                            borderColor="#e2e8f0"
                            cursor="pointer"
                            transition="all 0.2s"
                            _hover={{ bg: '#f1f5f9', borderColor: '#cbd5e1', color: '#475569' }}
                            _active={{ transform: 'scale(0.98)' }}
                            onClick={customOnClose}
                        >
                            Cancel
                        </Box>
                        <Box
                            as="button"
                            px={6}
                            py={2.5}
                            borderRadius="12px"
                            fontSize="14px"
                            fontWeight={600}
                            color="white"
                            bgGradient="linear(135deg, #00477D 0%, #1a5a94 100%)"
                            cursor={loading || !type || !description ? 'not-allowed' : 'pointer'}
                            opacity={loading || !type || !description ? 0.6 : 1}
                            transition="all 0.2s"
                            boxShadow="0 2px 8px rgba(0,71,125,0.3)"
                            _hover={!loading && type && description ? {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(0,71,125,0.4)',
                            } : {}}
                            _active={!loading && type && description ? { transform: 'scale(0.98)' } : {}}
                            onClick={handleSubmit}
                        >
                            {loading ? 'Submitting...' : 'Submit'}
                        </Box>
                    </Flex>
                </Box>
            </ModalContent>
        </Modal>
    );
};
